/**
 * First-church setup guide state — client-only, per church.
 *
 * Written when an owner creates their first church; consumed by WelcomeDialog and
 * SetupChecklist. Dismissing hides the guide for that church forever.
 */

export interface ChurchSetupState {
  welcomePending: boolean;
  completedSteps: string[];
  dismissed: boolean;
  collapsed: boolean;
}

export interface SetupStepConfig {
  id: string;
  titleKey: string;
  descKey: string;
  actionKey: string;
  actionPath: string;
  completesOnPath: string;
}

export const SETUP_STEPS: readonly SetupStepConfig[] = [
  {
    id: 'funds',
    titleKey: 'steps.funds.title',
    descKey: 'steps.funds.desc',
    actionKey: 'steps.funds.action',
    actionPath: '/giving/funds',
    completesOnPath: '/giving/funds',
  },
  {
    id: 'period',
    titleKey: 'steps.period.title',
    descKey: 'steps.period.desc',
    actionKey: 'steps.period.action',
    actionPath: '/giving/periods',
    completesOnPath: '/giving/periods',
  },
  {
    id: 'venue',
    titleKey: 'steps.venue.title',
    descKey: 'steps.venue.desc',
    actionKey: 'steps.venue.action',
    actionPath: '/settings/venues',
    completesOnPath: '/settings/venues',
  },
  {
    id: 'service',
    titleKey: 'steps.service.title',
    descKey: 'steps.service.desc',
    actionKey: 'steps.service.action',
    actionPath: '/services',
    completesOnPath: '/services',
  },
  {
    id: 'people',
    titleKey: 'steps.people.title',
    descKey: 'steps.people.desc',
    actionKey: 'steps.people.action',
    actionPath: '/settings/migration-assistant?onboarding=1',
    completesOnPath: '/settings/migration-assistant',
  },
  {
    id: 'staff',
    titleKey: 'steps.staff.title',
    descKey: 'steps.staff.desc',
    actionKey: 'steps.staff.action',
    actionPath: '/settings/staff',
    completesOnPath: '/settings/staff',
  },
] as const;

const storageKey = (churchId: string) => `trinity.setup.${churchId}`;

const defaultState = (): ChurchSetupState => ({
  welcomePending: false,
  completedSteps: [],
  dismissed: false,
  collapsed: false,
});

export const loadChurchSetup = (churchId: string): ChurchSetupState => {
  try {
    const raw = localStorage.getItem(storageKey(churchId));
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw) as Partial<ChurchSetupState>;
    return {
      welcomePending: Boolean(parsed.welcomePending),
      completedSteps: Array.isArray(parsed.completedSteps) ? parsed.completedSteps : [],
      dismissed: Boolean(parsed.dismissed),
      collapsed: Boolean(parsed.collapsed),
    };
  } catch {
    return defaultState();
  }
};

export const saveChurchSetup = (churchId: string, state: ChurchSetupState): void => {
  try {
    localStorage.setItem(storageKey(churchId), JSON.stringify(state));
  } catch {
    // Ignore quota / private-mode failures.
  }
};

/** Call when the user creates a church — starts welcome and the setup checklist. */
export const startFirstChurchSetup = (churchId: string): void => {
  saveChurchSetup(churchId, {
    welcomePending: true,
    completedSteps: [],
    dismissed: false,
    collapsed: false,
  });
};

export const isSetupDismissed = (churchId: string): boolean =>
  loadChurchSetup(churchId).dismissed;

export const dismissChurchSetup = (churchId: string): void => {
  const current = loadChurchSetup(churchId);
  saveChurchSetup(churchId, {
    ...current,
    welcomePending: false,
    dismissed: true,
  });
};

export const clearWelcomePending = (
  churchId: string,
  options?: { collapsed?: boolean },
): void => {
  const current = loadChurchSetup(churchId);
  saveChurchSetup(churchId, {
    ...current,
    welcomePending: false,
    collapsed: options?.collapsed ?? current.collapsed,
  });
};

export const setSetupCollapsed = (churchId: string, collapsed: boolean): void => {
  const current = loadChurchSetup(churchId);
  saveChurchSetup(churchId, { ...current, collapsed });
};

export const markSetupStepComplete = (churchId: string, stepId: string): ChurchSetupState => {
  const current = loadChurchSetup(churchId);
  if (current.completedSteps.includes(stepId)) return current;
  const next: ChurchSetupState = {
    ...current,
    completedSteps: [...current.completedSteps, stepId],
  };
  saveChurchSetup(churchId, next);
  return next;
};

export const hasChurchSetup = (churchId: string): boolean => {
  try {
    return localStorage.getItem(storageKey(churchId)) !== null;
  } catch {
    return false;
  }
};

/** True when first-church setup was started and not dismissed. */
export const shouldShowChurchSetup = (churchId: string): boolean => {
  if (!hasChurchSetup(churchId)) return false;
  return !loadChurchSetup(churchId).dismissed;
};
